import i18next from "i18next";

/**
 * Axios transport failures — the browser never got an HTTP response.
 *
 * `xhr.onerror` / abort / timeout become `AxiosError: Network Error` (or
 * `Request aborted`) with `code: ERR_NETWORK` / `ECONNABORTED` and
 * `response === undefined`. Those are offline, tab-kill, and flaky-mobile
 * drops — not partner 4xx/5xx. Real HTTP answers always carry `response`.
 *
 * Used to retry, toast calmly, and keep Sentry from paging FRONTEND-5
 * (POC-423).
 */
export const NETWORK_ERROR_TOAST =
	"Network error. Check your connection and try again.";

/**
 * `NETWORK_ERROR_TOAST` in the dashboard language, read at call time. Buyer
 * pages run in "en", so they keep the English line.
 */
export const networkErrorToast = (): string =>
	i18next.t(
		"common.errors.network",
		"Network error. Check your connection and try again.",
	) || NETWORK_ERROR_TOAST;

const TRANSPORT_CODES = new Set([
	"ERR_NETWORK",
	"ECONNABORTED",
	"ERR_ABORTED",
	"ETIMEDOUT",
	"ECONNRESET",
]);

const TRANSPORT_MESSAGE_RE =
	/^(Network Error|Request aborted|timeout of \d+ms exceeded)$/i;

export function isAxiosNetworkError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const err = error as {
		response?: unknown;
		code?: string;
		message?: string;
	};
	// Partner / API 4xx and 5xx must stay visible — never fold them into
	// "network error".
	if (err.response) return false;
	if (err.code && TRANSPORT_CODES.has(err.code)) return true;
	return (
		typeof err.message === "string" && TRANSPORT_MESSAGE_RE.test(err.message)
	);
}

export function isAxiosNetworkEventText(
	type?: string,
	value?: string,
): boolean {
	if (type !== "AxiosError") return false;
	return TRANSPORT_MESSAGE_RE.test(value ?? "");
}
